import React, { useState, useEffect } from "react";
import {
  CheckCircle,
  Eye,
  Search,
  X,
  Filter,
  RefreshCw,
  Gamepad2
} from "lucide-react";
import ActivateGameModal from "./components/ActivateGameModal";
import ViewGameModal from "./components/ViewGameModal";
import { useQuery } from "@tanstack/react-query";
import {
  getProducts,
  getProviders,
  getGameCategories,
  getGameTypes,
  getCountries
} from "../../../services/gameService";
import {
  Autocomplete,
  TextField,
  Chip,
  Button,
  CircularProgress,
  Alert
} from "@mui/material";
import redFocusStyles from "../../../components/ui/formUI/RedFocusedStyleV2";

const IMG_BASE_URL = import.meta.env.VITE_IMG_BASE_URL1;

function GameList() {
  const [openActivateModal, setOpenActivateModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedGameId, setSelectedGameId] = useState("");
  const [selectedGame, setSelectedGame] = useState({});
  const [playerId, setPlayerId] = useState("");

  const [filters, setFilters] = useState({
    search: "",
    countryId: "",
    productCategoryId: "",
    productTypeId: "",
    providerId: "",
    minPrice: "",
    maxPrice: "",
    status: ""
  });

  const [showFilters, setShowFilters] = useState(false);
  const [filteredGames, setFilteredGames] = useState([]);


  const [filterOptions, setFilterOptions] = useState({
    countries: [],
    providers: [],
    gameCategories: [],
    gameTypes: [],
  });


  const { data: gamesData, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["games", filters],
    queryFn: () => getProducts({
      countryId: filters.countryId,
      productCategoryId: filters.productCategoryId,
      productTypeId: filters.productTypeId,
      search: filters.search,
      productFor: "GAMES"
    }),
  });

  
  useQuery({
    queryKey: ["gameFilterOptions"],
    queryFn: async () => {
      try {
        const [countriesRes, providersRes, categoriesRes, typesRes] = await Promise.all([
          getCountries(),
          getProviders(),
          getGameCategories(),
          getGameTypes(),
        ]);

        setFilterOptions({
          countries: countriesRes?.data || [],
          providers: providersRes || [],
          gameCategories: categoriesRes?.data || [],
          gameTypes: typesRes?.data || [],
        });

        return true;
      } catch (err) {
        console.error("Error fetching filter options:", err);
        return false;
      }
    },
  });

  useEffect(() => {
    if (!gamesData?.data) return;

    const filtered = gamesData.data.filter(game => {
      const matchesSearch = !filters.search ||
        (game.productName?.toLowerCase().includes(filters.search.toLowerCase())) ||
        (game.description?.toLowerCase().includes(filters.search.toLowerCase()));

      const matchesCountry = !filters.countryId ||
        game.countryId === parseInt(filters.countryId);

      const matchesCategory = !filters.productCategoryId ||
        game.productCategoryId === parseInt(filters.productCategoryId);

      const matchesType = !filters.productTypeId ||
        game.productTypeId === parseInt(filters.productTypeId);

      const matchesProvider = !filters.providerId ||
        game.providerId === parseInt(filters.providerId);

      const matchesStatus = filters.status === "" ||
        game.status === (filters.status === "active");

      const price = parseFloat(game.price);
      const minPrice = filters.minPrice ? parseFloat(filters.minPrice) : -Infinity;
      const maxPrice = filters.maxPrice ? parseFloat(filters.maxPrice) : Infinity;
      const matchesPrice = price >= minPrice && price <= maxPrice;

      return matchesSearch &&
        matchesCountry &&
        matchesCategory &&
        matchesType &&
        matchesProvider &&
        matchesStatus &&
        matchesPrice;
    });

    setFilteredGames(filtered);
  }, [gamesData, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      search: "",
      countryId: "",
      productCategoryId: "",
      productTypeId: "",
      providerId: "",
      minPrice: "",
      maxPrice: "",
      status: ""
    });
  };

  const closeActivateModal = () => setOpenActivateModal(false);
  const closeViewModal = () => setOpenViewModal(false);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <CircularProgress />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" className="m-4">
        Error: {error.message}
      </Alert>
    );
  }

  return (
    <div className="w-full px-5 py-4 min-h-screen h-auto bg-[linear-gradient(135deg,_#EFF2FF_0%,_#FAF5FF_50%,_#FCF3FB_100%)] dark:bg-[linear-gradient(135deg,_#1e293b_0%,_#334155_50%,_#0f172a_100%)]">
      <ActivateGameModal
        open={openActivateModal}
        onClose={closeActivateModal}
        game={selectedGame}
        playerId={playerId}
        setPlayerId={setPlayerId}
      />
      
      <ViewGameModal
        gameId={selectedGameId}
        open={openViewModal}
        onClose={closeViewModal}
      />

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold dark:text-white">Game Products</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Activate game credits for players
          </p>
        </div>
        
        <div className="flex items-center mt-3 sm:mt-0 gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {filteredGames.length} games available
          </span>
          <Button
            onClick={() => refetch()}
            variant="outlined"
            sx={{borderColor: "#CD0202", color: "#CD0202"}}
            size="small"
            startIcon={<RefreshCw size={16} />}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-1 min-w-[300px]">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={20} className="text-gray-400 dark:text-gray-500" />
          </div>
          <input
            type="text"
            placeholder="Search games by name, description or price..."
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
        </div>
        
        <Button
          onClick={() => setShowFilters(!showFilters)}
          variant={showFilters ? "outlined" : "outlined"}
          sx={{borderColor: "#CD0202", color: "#CD0202"}}
          startIcon={<Filter size={20} />}
        >
          {showFilters ? "Hide Filters" : "Show Filters"}
        </Button>
      </div>


      {showFilters && (
        <div className="mb-6 p-5 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold dark:text-white">Filter Games</h3>
            <div className="flex gap-3">
              <Button
                onClick={resetFilters}
                color="error"
                variant="text"
                size="small"
                sx={{ color: "#CD0202" }}
              >
                Reset filters
              </Button>
              <Button
                onClick={() => setShowFilters(false)}
                variant="text"
                size="small"
                sx={{ color: "#6B7280" }}
              >
                <X size={20} />
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Country Filter */}
            <div>
              <label className="block text-sm font-medium mb-2 dark:text-gray-300">
                Country
              </label>
              <Autocomplete
                options={filterOptions.countries}
                getOptionLabel={(option) => option.countryName || ""}
                value={filterOptions.countries.find(c => c.id === filters.countryId) || null}
                onChange={(event, newValue) =>
                  handleFilterChange("countryId", newValue?.id || "")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select country"
                    variant="outlined"
                    size="small"
                    sx={redFocusStyles}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} className="px-4 py-2 hover:bg-red-50 dark:hover:bg-slate-700 dark:text-white">
                    {option.countryName}
                  </li>
                )}
              />
            </div>

            {/* Game Category Filter */}
            <div>
              <label className="block text-sm font-medium mb-2 dark:text-gray-300">
                Game Category
              </label>
              <Autocomplete
                options={filterOptions.gameCategories}
                getOptionLabel={(option) => option.category_name?.en || option.category_name || ""}
                value={filterOptions.gameCategories.find(c => c.id === filters.productCategoryId) || null}
                onChange={(event, newValue) =>
                  handleFilterChange("productCategoryId", newValue?.id || "")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select category"
                    variant="outlined"
                    size="small"
                    sx={redFocusStyles}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} className="px-4 py-2 hover:bg-red-50 dark:hover:bg-slate-700 dark:text-white">
                    {option.category_name?.en || option.category_name}
                  </li>
                )}
              />
            </div>

            {/* Game Type Filter */}
            <div>
              <label className="block text-sm font-medium mb-2 dark:text-gray-300">
                Game Type
              </label>
              <Autocomplete
                options={filterOptions.gameTypes}
                getOptionLabel={(option) => option.productType?.en || option.productType || ""}
                value={filterOptions.gameTypes.find(t => t.id === filters.productTypeId) || null}
                onChange={(event, newValue) =>
                  handleFilterChange("productTypeId", newValue?.id || "")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select type"
                    variant="outlined"
                    size="small"
                    sx={redFocusStyles}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} className="px-4 py-2 hover:bg-red-50 dark:hover:bg-slate-700 dark:text-white">
                    {option.productType?.en || option.productType}
                  </li>
                )}
              />
            </div>

            {/* Provider Filter */}
            <div>
              <label className="block text-sm font-medium mb-2 dark:text-gray-300">
                Provider
              </label>
              <Autocomplete
                options={filterOptions.providers}
                getOptionLabel={(option) => option.name || ""}
                value={filterOptions.providers.find(p => p.id === filters.providerId) || null}
                onChange={(event, newValue) =>
                  handleFilterChange("providerId", newValue?.id || "")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select provider"
                    variant="outlined"
                    size="small"
                    sx={redFocusStyles}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} className="px-4 py-2 hover:bg-red-50 dark:hover:bg-slate-700 dark:text-white">
                    {option.name}
                  </li>
                )}
              />
            </div>

            {/* Price Range Filter */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2 dark:text-gray-300">
                Price Range (AFN)
              </label>
              <div className="flex gap-2">
                <TextField
                  type="number"
                  placeholder="Min"
                  value={filters.minPrice}
                  onChange={(e) => handleFilterChange("minPrice", e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={redFocusStyles}
                  fullWidth
                />
                <TextField
                  type="number"
                  placeholder="Max"
                  value={filters.maxPrice}
                  onChange={(e) => handleFilterChange("maxPrice", e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={redFocusStyles}
                  fullWidth
                />
              </div>
            </div>

   
            <div>
              <label className="block text-sm font-medium mb-2 dark:text-gray-300">
                Status
              </label>
              <Autocomplete
                options={[
                  { label: "Active", value: "active" },
                  { label: "Inactive", value: "inactive" }
                ]}
                getOptionLabel={(option) => option.label}
                value={filters.status ? { label: filters.status === "active" ? "Active" : "Inactive", value: filters.status } : null}
                onChange={(event, newValue) =>
                  handleFilterChange("status", newValue?.value || "")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select status"
                    variant="outlined"
                    size="small"
                    sx={redFocusStyles}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} className="px-4 py-2 hover:bg-red-50 dark:hover:bg-slate-700 dark:text-white">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${
                        option.value === "active" ? "bg-green-500" : "bg-red-500"
                      }`}></div>
                      {option.label}
                    </div>
                  </li>
                )}
              />
            </div>
          </div>
        </div>
      )}

      {filteredGames.length === 0 ? (
        <div className="text-center py-10">
          <div className="bg-gray-100 dark:bg-slate-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Gamepad2 size={32} className="text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
            No games found
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Try adjusting your search or filter criteria
          </p>
          <Button
            onClick={resetFilters}
            variant="outlined"
            color="primary"
            sx={{borderColor: "#CD0202", color: "#CD0202"}}
          >
            Clear all filters
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredGames.map((game) => (
            <div
              key={game.id}
              className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-slate-800 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
            
              <div className="h-48 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-slate-700 dark:to-slate-800 relative">
                {game.image ? (
                  <img
                    src={`${IMG_BASE_URL}/uploads/product_images/${game.image}`}
                    alt={game.productName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = "https://via.placeholder.com/150";
                      e.currentTarget.onerror = null;
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Gamepad2 size={48} className="text-gray-400 dark:text-gray-500" />
                  </div>
                )}
                <div className="absolute top-3 right-3">
                  <Chip
                    label={game.status ? "Active" : "Inactive"}
                    size="small"
                    color={game.status ? "success" : "error"}
                    className={game.status ? "dark:bg-green-900 dark:text-green-200" : "dark:bg-red-900 dark:text-red-200"}
                  />
                </div>
              </div>

              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white line-clamp-1">
                    {game.productName?.en}
                  </h3>
                  <div className="text-xl font-bold text-red-600 dark:text-red-400">
                    {game.price} AFN
                  </div>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                  {game.description || "No description available"}
                </p>

 
                <div className="flex flex-wrap gap-1 mb-4">
                  {game.tag?.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-300 rounded-full text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

           
                <div className="flex gap-2">
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{borderColor: "#CD0202", color: "#CD0202"}}
                    size="small"
                    startIcon={<Eye size={16} />}
                    onClick={() => {
                      setSelectedGameId(game.id);
                      setOpenViewModal(true);
                    }}
                  >
                    View
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    sx={{backgroundColor:"#CD0202", "&:hover": { backgroundColor: "#B00000" }}}
                    size="small"
                    startIcon={<CheckCircle size={16} />}
                    onClick={() => {
                      setSelectedGame(game);
                      setOpenActivateModal(true);
                    }}
                    disabled={!game.status}
                  >
                    Activate
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default GameList;